import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Stack,
  Alert,
  Snackbar,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import ProductGallery from "./ProductGallery";
import ProductVariants from "./ProductVariants";
import ProductBuyBox from "./ProductBuyBox";
import RelatedProducts from "./RelatedProducts";
import ProductComments from "./ProductComments";
import BreadcrumbsBar from "../Utils/BreadcrumbsBar";
import DiscountTimer from "./DiscountTimer";
import InventoryRequestNotification from "./InventoryRequestNotification";

export default function ProductDetailUI({
  product,
  selectedVariant,
  onSelectVariant,
  cartQuantity,
  handleRequestInventoryCheck,
  requestQuantity,
  setRequestQuantity,
  requestLoading,
  isFavorite,
  favoriteLoading,
  handleToggleFavorite,
  actionError,
  setActionError,
  requestSuccess,
  setRequestSuccess,
  requestAlreadySubmitted,
  inventoryNotification,
  onDismissInventoryNotification,
  onViewInventoryDetails,
  inventoryStatus,
}) {
  // State to manage current active tab (0 = Description, 1 = Comments)
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [product?.id]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const originalUnitPrice = Number(selectedVariant?.price || 0);

  const discountedUnitPrice = Number(
    selectedVariant?.discounted_price ?? selectedVariant?.price ?? 0,
  );

  const hasDiscount = Boolean(selectedVariant?.has_discount);

  const discountPercent = Number(selectedVariant?.discount_percent || 0);

  const payableUnitPrice = hasDiscount
    ? discountedUnitPrice
    : originalUnitPrice;

  const unitsPerPackage = Math.max(
    1,
    Number(selectedVariant?.units_per_package ?? 1),
  );

  const quantity = Math.max(1, Number(requestQuantity) || 1);

  const originalPackagePrice = originalUnitPrice * unitsPerPackage;

  const payablePackagePrice = payableUnitPrice * unitsPerPackage;

  // Total price for the requested number of packages.
  const originalTotalPrice = originalPackagePrice * quantity;

  const payableTotalPrice = payablePackagePrice * quantity;

  const toToman = (amountInToman) => Math.floor(Number(amountInToman || 0));

  // 2. Format breadcrumbs for BreadcrumbsBar
  const formattedBreadcrumbs = useMemo(() => {
    if (!product) return [];

    const list = [
      { id: "home", title: "فروشگاه اینترنتی زومیو" },
      ...(product.breadcrumbs || []).map((b, idx) => ({
        id: b.slug || `cat-${idx}`,
        title: b.title,
        slug: b.slug,
      })),
      { id: `product-${product.id}`, title: product.title },
    ];

    return list;
  }, [product]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: 4,
        mb: { xs: 12, lg: 8 },
        direction: "rtl",
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "clip",
      }}
    >
      <Snackbar
        open={Boolean(actionError || requestSuccess)}
        autoHideDuration={4000}
        onClose={() => {
          setActionError(null);
          setRequestSuccess("");
        }}
      >
        {actionError ? (
          <Alert severity="error">{actionError}</Alert>
        ) : (
          <Alert severity="success">{requestSuccess}</Alert>
        )}
      </Snackbar>

      {inventoryNotification && (
        <InventoryRequestNotification
          notification={inventoryNotification}
          onDismiss={onDismissInventoryNotification}
          onViewDetails={onViewInventoryDetails}
        />
      )}

      {/* 3. Render BreadcrumbsBar at top */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <BreadcrumbsBar breadcrumbs={formattedBreadcrumbs} />
      </Box>

      {/* TOP SECTION: Gallery, Variants & Buy Box */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 4,
          border: "1px solid",
          borderColor: "grey.200",
          mb: 4,
        }}
      >
        <Grid container spacing={{ xs: 4, md: 4, lg: 6 }}>
          {/* GALLERY COLUMN */}
          <Grid size={{ xs: 12, md: 5, lg: 4 }}>
            <ProductGallery
              product={product}
              isFavorite={isFavorite}
              favoriteLoading={favoriteLoading}
              handleToggleFavorite={handleToggleFavorite}
              setActionError={setActionError}
            />
          </Grid>

          {/* VARIANTS & DETAILS COLUMN */}
          <Grid size={{ xs: 12, md: 7, lg: 5 }}>
            <Typography
              variant="subtitle2"
              color="primary.main"
              fontWeight={700}
            >
              {product?.brand}
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
              {product?.title}
            </Typography>

            {/* Package price and per-item price */}
            {selectedVariant && (
              <Box sx={{ mb: 2 }}>
                {hasDiscount ? (
                  <Stack spacing={0.75}>
                    {/* Original price + discount badge */}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textDecoration: "line-through" }}
                      >
                        {toToman(originalTotalPrice).toLocaleString("en-US")}{" "}
                        تومان
                      </Typography>
                      <Chip
                        label={
                          discountPercent > 0
                            ? `٪${discountPercent} تخفیف`
                            : "تخفیف‌دار"
                        }
                        color="error"
                        size="small"
                        sx={{ fontWeight: 800, borderRadius: 1.5 }}
                      />
                    </Box>

                    {/* Actual selling price */}
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      color="error.main"
                    >
                      {toToman(payableTotalPrice).toLocaleString("en-US")}
                      <Typography
                        component="span"
                        variant="caption"
                        fontWeight={600}
                      >
                        تومان
                      </Typography>
                    </Typography>

                    {/* NEW: Insert the Countdown Timer here */}
                    {selectedVariant?.discounts?.[0]?.end_date && (
                      <DiscountTimer
                        endDateStr={selectedVariant.discounts[0].end_date}
                      />
                    )}
                  </Stack>
                ) : (
                  /* Normal price */
                  <Typography variant="h6" fontWeight={800}>
                    {toToman(payableTotalPrice).toLocaleString("en-US")}
                    <Typography
                      component="span"
                      variant="caption"
                      fontWeight={600}
                    >
                      تومان
                    </Typography>
                  </Typography>
                )}

                {unitsPerPackage > 1 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    بسته {unitsPerPackage.toLocaleString("en-US")} عددی
                    {" · "}
                    هر عدد {toToman(payableUnitPrice).toLocaleString(
                      "en-US",
                    )}{" "}
                    تومان
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Warning: Prices are approximate  */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 1,
                lineHeight: 1.8,
                textAlign: "right",
              }}
              dir="rtl"
            >
              ⚠️ قیمت‌ها تقریبی هستند و ممکن است به‌روز نباشند. لطفاً قبل از
              خرید، قیمت نهایی را از فروشنده استعلام بگیرید.
            </Typography>

            <ProductVariants
              variants={product?.product_variants}
              attributes={product?.attributes}
              selectedVariant={selectedVariant}
              onSelectVariant={onSelectVariant}
            />
          </Grid>

          {/* BUY BOX COLUMN */}
          <Grid size={{ xs: 12, md: 12, lg: 3 }}>
            <ProductBuyBox
              selectedVariant={selectedVariant}
              requestQuantity={requestQuantity}
              setRequestQuantity={setRequestQuantity}
              handleRequestInventoryCheck={handleRequestInventoryCheck}
              requestLoading={requestLoading}
              requestAlreadySubmitted={requestAlreadySubmitted}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* TABBED SECTION: Description & Comments */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 4,
          border: "1px solid",
          borderColor: "grey.200",
          mb: 4,
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              label="توضیحات محصول"
              sx={{ fontWeight: 700, fontSize: "1rem" }}
            />
            <Tab
              label="نظرات کاربران"
              sx={{ fontWeight: 700, fontSize: "1rem" }}
            />
          </Tabs>
        </Box>

        {/* Tab 1: Description & Tags */}
        {activeTab === 0 && (
          <Box p={1}>
            {product?.description ? (
              <Typography
                variant="body1"
                color="text.secondary"
                lineHeight={2}
                sx={{ whiteSpace: "pre-line" }}
              >
                {product.description}
              </Typography>
            ) : (
              <Alert severity="info">
                توضیحات تکمیلی برای این محصول ثبت نشده است.
              </Alert>
            )}

            {/* Render Tags inside the Description tab */}
            {product?.tags?.length > 0 && (
              <Box
                mt={4}
                pt={2}
                sx={{ borderTop: "1px dashed", borderColor: "grey.300" }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  mb={1.5}
                  color="text.secondary"
                >
                  برچسب‌ها:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {product.tags.map((tag, idx) => (
                    <Chip
                      key={idx}
                      label={`# ${tag}`}
                      size="small"
                      variant="outlined"
                      clickable
                      sx={{ borderRadius: "6px", fontWeight: 600 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 2: Comments */}
        {activeTab === 1 && (
          <Box p={1}>
            <ProductComments productId={product?.id} />
          </Box>
        )}
      </Paper>

      {/* RELATED PRODUCTS */}
      <RelatedProducts relatedProducts={product?.related_products} />
    </Container>
  );
}
